import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import { StyledCard } from '../components/ui/StyledComponents';
import SectionDivider from '../components/ui/SectionDivider';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import ClassIcon from '@mui/icons-material/Class';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ClassStudentAttendanceChart from '../components/ClassStudentAttendanceChart';
import ClassStudentQuranProgressChart from '../components/ClassStudentQuranProgressChart';
import ProgressModeToggle from '../components/ui/ProgressModeToggle';
import supabase from '../supabaseClient';

function AdminDashboard() {
  const { userRole } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [selectedClassId, setSelectedClassId] = useState('all');
  const [progressMode, setProgressMode] = useState('verses');
  const [classes, setClasses] = useState([]);
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalTeachers: 0,
    totalPendingWaitlist: 0,
    totalClasses: 0
  });

  // Fetch stats and classes
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch classes
        const { data: classesData, error: classesError } = await supabase
          .from('classes')
          .select('id, name')
          .order('name');

        if (classesError) {
          console.error('Error fetching classes:', classesError);
        } else if (classesData) {
          setClasses(classesData);
        }

        // Fetch stats in parallel
        const [studentsResult, teachersResult, waitlistResult, classesCountResult] = await Promise.all([
          supabase
            .from('students')
            .select('id', { count: 'exact', head: true })
            .eq('is_active', true),
          supabase
            .from('teachers')
            .select('id', { count: 'exact', head: true })
            .eq('is_active', true),
          supabase
            .from('waitlist')
            .select('id', { count: 'exact', head: true })
            .eq('status', 'pending'),
          supabase
            .from('classes')
            .select('id', { count: 'exact', head: true })
        ]);

        // Update stats
        setStats({
          totalStudents: studentsResult.count || 0,
          totalTeachers: teachersResult.count || 0,
          totalPendingWaitlist: waitlistResult.count || 0,
          totalClasses: classesCountResult.count || 0
        });

        // Log any errors
        if (studentsResult.error) console.error('Error fetching students count:', studentsResult.error);
        if (teachersResult.error) console.error('Error fetching teachers count:', teachersResult.error);
        if (waitlistResult.error) console.error('Error fetching pending waitlist count:', waitlistResult.error);
        if (classesCountResult.error) console.error('Error fetching classes count:', classesCountResult.error);

      } catch (error) {
        console.error('Error in fetchData:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleClassChange = (event) => {
    setSelectedClassId(event.target.value);
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6">Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 1, sm: 2 },
        }}
      >
        Admin Dashboard
        {userRole === 'super_admin' && (
          <Chip 
            label="Super Admin" 
            color="secondary" 
            size="small" 
            sx={{ ml: { xs: 0, sm: 2 } }}
          />
        )}
      </Typography>

      {/* Main Content */}
      <Box>
          {/* Stats Cards Section */}
          <Grid container spacing={2} sx={{ mb: { xs: 3, sm: 4 } }}>
            <Grid item xs={12} sm={6} md={3}>
          <StyledCard sx={{
            background: theme.palette.background.paper,
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
            borderRadius: 2,
            p: { xs: 1.5, sm: 2 },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
              <Box
                sx={{
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  borderRadius: 1.5,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(52, 152, 219, 0.2)' : 'rgba(52, 152, 219, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <SchoolIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: theme.palette.primary.main }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '0.7rem', sm: '0.75rem' }, lineHeight: 1 }}>
                  Students
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2, mt: 0.5, fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>
                  {stats.totalStudents}
                </Typography>
              </Box>
            </Box>
          </StyledCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StyledCard sx={{
            background: theme.palette.background.paper,
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
            borderRadius: 2,
            p: { xs: 1.5, sm: 2 },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
              <Box
                sx={{
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  borderRadius: 1.5,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(39, 174, 96, 0.2)' : 'rgba(39, 174, 96, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <PeopleIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#27ae60' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '0.7rem', sm: '0.75rem' }, lineHeight: 1 }}>
                  Teachers
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2, mt: 0.5, fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>
                  {stats.totalTeachers}
                </Typography>
              </Box>
            </Box>
          </StyledCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StyledCard sx={{
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                borderRadius: 2,
                p: { xs: 1.5, sm: 2 },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                  <Box
                    sx={{
                      width: { xs: 36, sm: 40 },
                      height: { xs: 36, sm: 40 },
                      borderRadius: 1.5,
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(230, 126, 34, 0.2)' : 'rgba(230, 126, 34, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <ClassIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#e67e22' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '0.7rem', sm: '0.75rem' }, lineHeight: 1 }}>
                      Classes
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2, mt: 0.5, fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>
                      {stats.totalClasses}
                    </Typography>
                  </Box>
                </Box>
              </StyledCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
          <StyledCard sx={{
            background: theme.palette.background.paper,
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
            borderRadius: 2,
            p: { xs: 1.5, sm: 2 },
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
              <Box
                sx={{
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  borderRadius: 1.5,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(155, 89, 182, 0.2)' : 'rgba(155, 89, 182, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <ListAltIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#9b59b6' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '0.7rem', sm: '0.75rem' }, lineHeight: 1 }}>
                  Pending Waitlist
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2, mt: 0.5, fontSize: { xs: '1.5rem', sm: '1.75rem' } }}>
                  {stats.totalPendingWaitlist}
                </Typography>
              </Box>
            </Box>
          </StyledCard>
            </Grid>
          </Grid>

          {/* Management Cards Section */}
          <Grid container spacing={3} sx={{ mb: { xs: 3, sm: 4 } }}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <StyledCard
            onClick={() => navigate('/admin/management')}
            sx={{
              cursor: 'pointer',
              position: 'relative',
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
              borderRadius: 3,
              transition: 'all 0.3s ease',
              transform: 'translateY(0)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.palette.mode === 'dark' 
                  ? '0 12px 40px rgba(0, 0, 0, 0.5)' 
                  : '0 12px 40px rgba(0, 0, 0, 0.15)',
                borderColor: '#3498db',
              },
            }}
          >
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: 1.5, sm: 2 } }}>
                <Box
                  sx={{
                    width: { xs: 44, sm: 52 },
                    height: { xs: 44, sm: 52 },
                    borderRadius: 2,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(52, 152, 219, 0.2)' : 'rgba(52, 152, 219, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: { xs: 1.5, sm: 2 },
                    flexShrink: 0,
                  }}
                >
                  <GroupAddIcon sx={{ fontSize: { xs: 20, sm: 24 }, color: '#3498db' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    Enrollment
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    System Control
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '0.75rem', sm: '0.8rem' }, lineHeight: 1.4 }}>
                Add new students, guardians, and classes
              </Typography>
            </Box>
              </StyledCard>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <StyledCard
                onClick={() => navigate('/admin/waitlist')}
            sx={{
              cursor: 'pointer',
              position: 'relative',
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
              borderRadius: 3,
              transition: 'all 0.3s ease',
              transform: 'translateY(0)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.palette.mode === 'dark' 
                  ? '0 12px 40px rgba(0, 0, 0, 0.5)' 
                  : '0 12px 40px rgba(0, 0, 0, 0.15)',
                borderColor: '#9b59b6',
              },
            }}
          >
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: 1.5, sm: 2 } }}>
                <Box
                  sx={{
                    width: { xs: 44, sm: 52 },
                    height: { xs: 44, sm: 52 },
                    borderRadius: 2,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(155, 89, 182, 0.2)' : 'rgba(155, 89, 182, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: { xs: 1.5, sm: 2 },
                    flexShrink: 0,
                  }}
                >
                  <ListAltIcon sx={{ fontSize: { xs: 20, sm: 24 }, color: '#9b59b6' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    Waitlist
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    Student Waitlist
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '0.75rem', sm: '0.8rem' }, lineHeight: 1.4 }}>
                Review and process waitlist entries
              </Typography>
            </Box>
              </StyledCard>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <StyledCard
                onClick={() => navigate('/admin/teacher-activity')}
            sx={{
              cursor: 'pointer',
              position: 'relative',
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
              borderRadius: 3,
              transition: 'all 0.3s ease',
              transform: 'translateY(0)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.palette.mode === 'dark' 
                  ? '0 12px 40px rgba(0, 0, 0, 0.5)' 
                  : '0 12px 40px rgba(0, 0, 0, 0.15)',
                borderColor: '#27ae60',
              },
            }}
          >
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: 1.5, sm: 2 } }}>
                <Box
                  sx={{
                    width: { xs: 44, sm: 52 },
                    height: { xs: 44, sm: 52 },
                    borderRadius: 2,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(39, 174, 96, 0.2)' : 'rgba(39, 174, 96, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: { xs: 1.5, sm: 2 },
                    flexShrink: 0,
                  }}
                >
                  <PeopleIcon sx={{ fontSize: { xs: 20, sm: 24 }, color: '#27ae60' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    Teacher Activity
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    Performance Tracking
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '0.75rem', sm: '0.8rem' }, lineHeight: 1.4 }}>
                Monitor teacher engagement and activity
              </Typography>
            </Box>
              </StyledCard>
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <StyledCard
                onClick={() => navigate('/admin/substitute-management')}
            sx={{
              cursor: 'pointer',
              position: 'relative',
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
              borderRadius: 3,
              transition: 'all 0.3s ease',
              transform: 'translateY(0)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: theme.palette.mode === 'dark' 
                  ? '0 12px 40px rgba(0, 0, 0, 0.5)' 
                  : '0 12px 40px rgba(0, 0, 0, 0.15)',
                borderColor: '#e67e22',
              },
            }}
          >
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: { xs: 1.5, sm: 2 } }}>
                <Box
                  sx={{
                    width: { xs: 44, sm: 52 },
                    height: { xs: 44, sm: 52 },
                    borderRadius: 2,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(230, 126, 34, 0.2)' : 'rgba(230, 126, 34, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: { xs: 1.5, sm: 2 },
                    flexShrink: 0,
                  }}
                >
                  <AutorenewIcon sx={{ fontSize: { xs: 20, sm: 24 }, color: '#e67e22' }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                    Substitutes
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    Assignment Control
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '0.75rem', sm: '0.8rem' }, lineHeight: 1.4 }}>
                Manage substitute teacher assignments
              </Typography>
            </Box>
              </StyledCard>
            </Grid>
          </Grid>

          {userRole === 'super_admin' && (
            <Alert severity="info" sx={{ mb: { xs: 3, sm: 4 } }}>
              As a super admin, you have access to all administrative functions.
            </Alert>
          )}

          {/* Section Divider */}
          <SectionDivider />

          {/* Analytics Section */}
          <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: 3,
        gap: { xs: 2, sm: 0 },
      }}>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 0,
            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
          }}
        >
          Analytics
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 2 },
          alignItems: { xs: 'stretch', sm: 'center' },
          width: { xs: '100%', sm: 'auto' },
        }}>
          {classes.length > 0 && (
            <FormControl sx={{ minWidth: { xs: '100%', sm: 200 }, width: { xs: '100%', sm: 'auto' } }}>
              <InputLabel id="class-select-label">Filter by Class</InputLabel>
              <Select
                labelId="class-select-label"
                id="class-select"
                value={selectedClassId || 'all'}
                label="Filter by Class"
                onChange={handleClassChange}
                size="small"
              >
                <MenuItem value="all">All Classes</MenuItem>
                {classes.map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>
                    {cls.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <ProgressModeToggle value={progressMode} onChange={(event, newValue) => {
            if (newValue !== null) {
              setProgressMode(newValue);
            }
          }} />
        </Box>
          </Box>
          
          <Grid container spacing={3}>

            {/* Attendance Analytics */}
            <Grid item xs={12} md={6}>
          <StyledCard sx={{
            borderRadius: 3,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
            },
          }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              {selectedClassId === 'all' ? 'Overall' : 'Class'} Attendance Overview
            </Typography>
            <Box sx={{ flex: 1, minHeight: { xs: 300, sm: 400, md: 500 } }}>
              <ClassStudentAttendanceChart 
                classId={selectedClassId}
                isAdmin={true}
              />
            </Box>
              </StyledCard>
            </Grid>
            
            {/* Quran Progress Analytics */}
            <Grid item xs={12} md={6}>
              <StyledCard sx={{
            borderRadius: 3,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
            },
          }}>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              {selectedClassId === 'all' ? 'Overall' : 'Class'} Quran Progress Overview
            </Typography>
            <Box sx={{ flex: 1, minHeight: { xs: 300, sm: 400, md: 500 } }}>
              <ClassStudentQuranProgressChart 
                classId={selectedClassId}
                isAdmin={true}
                progressMode={progressMode}
              />
            </Box>
              </StyledCard>
            </Grid>
          </Grid>
        </Box>
    </Box>
  );
}

export default AdminDashboard; 